import { PaymentFrequency } from '../../__generated__/types';

export type PlanAudience = 'designer' | 'homeowner';

export interface PlanConfiguration {
  tier: 'Home' | 'Design' | 'Design+' | 'Basic' | 'Guild' | 'Guild+';
  frequency: PaymentFrequency;
  enabled: boolean;
  comingSoon?: boolean;
  priority: number; // Lower numbers show first
  audience: PlanAudience;
}

// Define which plans should be displayed and their status
export const PLAN_CONFIGURATIONS: PlanConfiguration[] = [
  // Designer: single marketing membership
  {
    tier: 'Guild',
    frequency: PaymentFrequency.Annually,
    enabled: true,
    priority: 1,
    audience: 'designer',
  },

  // Homeowner Tier
  {
    tier: 'Basic',
    frequency: PaymentFrequency.Monthly,
    enabled: true,
    priority: 10,
    audience: 'homeowner',
  },
  {
    tier: 'Guild',
    frequency: PaymentFrequency.Monthly,
    enabled: true,
    priority: 11,
    audience: 'homeowner',
  },
  {
    tier: 'Guild+',
    frequency: PaymentFrequency.Annually,
    enabled: true,
    priority: 12,
    audience: 'homeowner',
  },
];

// Helper functions
export const getPlanConfiguration = (tier: string, frequency: PaymentFrequency): PlanConfiguration | undefined => {
  return PLAN_CONFIGURATIONS.find(
    config => config.tier === tier && config.frequency === frequency
  );
};

export const getEnabledConfigurations = (): PlanConfiguration[] => {
  return PLAN_CONFIGURATIONS.filter(config => config.enabled).sort((a, b) => a.priority - b.priority);
};

export const getAllConfigurations = (): PlanConfiguration[] => {
  return PLAN_CONFIGURATIONS.sort((a, b) => a.priority - b.priority);
};

export const getConfigurationsByFrequency = (
  frequency: PaymentFrequency,
  audience: PlanAudience = 'designer'
): PlanConfiguration[] => {
  return PLAN_CONFIGURATIONS
    .filter(config => config.frequency === frequency && config.audience === audience)
    .sort((a, b) => a.priority - b.priority);
};

export const getConfigurationsByAudience = (audience: PlanAudience): PlanConfiguration[] => {
  return PLAN_CONFIGURATIONS
    .filter(config => config.audience === audience)
    .sort((a, b) => a.priority - b.priority);
};

// Environment variable mapping for HighLevel product IDs
export const getProductIdForPlan = (tier: string, frequency: PaymentFrequency): string | undefined => {
  const envKey = `REACT_APP_HL_${tier.toUpperCase().replace('+', 'PLUS')}_${
    frequency === PaymentFrequency.BiAnnually ? 'BIANNUAL' :
    frequency === PaymentFrequency.Annually ? 'ANNUAL' : 'MONTHLY'
  }_ID`;
  
  return process.env[envKey];
};

// Check if a plan should be displayed based on configuration
export const shouldShowPlan = (tier: string, frequency: PaymentFrequency): boolean => {
  const config = getPlanConfiguration(tier, frequency);
  return config ? config.enabled : false;
};

// Check if a plan should show as placeholder
export const shouldShowPlaceholder = (tier: string, frequency: PaymentFrequency): boolean => {
  const config = getPlanConfiguration(tier, frequency);
  return config ? !config.enabled : true;
};
